/**
 * The GitHub search translator for the scope-filter builder, beside the JQL
 * one. Same contract (see `../scopeFilter`): `compile` writes only the
 * filter fragment, and `parse` reads back exactly what `compile` writes, so
 * anything else opens in the Advanced view byte for byte and is never
 * rewritten.
 *
 * The server puts the fragment inside its own parentheses next to the bound
 * repositories (`is:issue (repo:a OR repo:b) (<fragment>)`), using GitHub's
 * advanced search, so the fragment can only narrow. That is why "match any"
 * and "is any of" may use `OR` here. The server also refuses text that names
 * repositories, owners, pull requests or an order; nothing here writes those.
 *
 * Written terms, per field:
 *   Labels     label:a · (label:a OR label:b) · -label:a · (-label:a AND -label:b) · no:label
 *   Assignee   assignee:x · … · no:assignee
 *   Milestone  milestone:v2 · … · no:milestone
 *   State      is:open · is:closed
 *   Closed as  reason:completed · reason:not-planned
 *   Created, Updated   created:<2026-09-01 · created:>2026-09-01
 * Conditions join with ` AND ` (match all) or ` OR ` (match any).
 *
 * GitHub search has no "in the last N days", so its dates offer before and
 * after only.
 */
import type {
  BuilderModel,
  Condition,
  FieldConfig,
  OperatorKey,
  ScopeFilterModel,
  ScopeFilterTranslator,
} from '../scopeFilter'

export type GitHubFieldKey = 'labels' | 'assignee' | 'milestone' | 'state' | 'reason' | 'created' | 'updated'

interface GitHubFieldConfig extends FieldConfig {
  key: GitHubFieldKey
  /** The search qualifier the field writes. */
  qualifier: string
}

const SET_OPS: OperatorKey[] = ['isAnyOf', 'isNotAnyOf', 'isEmpty']
const DATE_OPS: OperatorKey[] = ['before', 'after']

/** The values of the two one-of-a-list fields, as shown, and what each
 * writes. */
const STATE_VALUES: Record<string, string> = { Open: 'open', Closed: 'closed' }
const REASON_VALUES: Record<string, string> = { Completed: 'completed', 'Not planned': 'not-planned' }

const FIELD_CONFIGS: Record<GitHubFieldKey, GitHubFieldConfig> = {
  labels: {
    key: 'labels', qualifier: 'label', label: 'Labels', valueKind: 'labels', operators: SET_OPS,
    operatorLabels: { isNotAnyOf: 'is none of', isEmpty: 'has none' },
  },
  assignee: {
    key: 'assignee', qualifier: 'assignee', label: 'Assignee', valueKind: 'user', operators: SET_OPS,
    operatorLabels: { isNotAnyOf: 'is none of' },
  },
  milestone: {
    key: 'milestone', qualifier: 'milestone', label: 'Milestone', valueKind: 'versions', operators: SET_OPS,
    operatorLabels: { isNotAnyOf: 'is none of', isEmpty: 'has none' }, addPlaceholder: '+ milestone',
  },
  state: { key: 'state', qualifier: 'is', label: 'State', valueKind: 'fixed', operators: ['is'], options: Object.keys(STATE_VALUES) },
  reason: { key: 'reason', qualifier: 'reason', label: 'Closed as', valueKind: 'fixed', operators: ['is'], options: Object.keys(REASON_VALUES) },
  created: { key: 'created', qualifier: 'created', label: 'Created', valueKind: 'date', operators: DATE_OPS },
  updated: { key: 'updated', qualifier: 'updated', label: 'Updated', valueKind: 'date', operators: DATE_OPS },
}

/** Field list for the builder's field dropdown, in the mockup's order. */
export const GITHUB_FIELDS: ReadonlyArray<GitHubFieldConfig> = [
  FIELD_CONFIGS.labels,
  FIELD_CONFIGS.assignee,
  FIELD_CONFIGS.milestone,
  FIELD_CONFIGS.state,
  FIELD_CONFIGS.reason,
  FIELD_CONFIGS.created,
  FIELD_CONFIGS.updated,
]

/** The set fields by qualifier, for parse. */
const SET_FIELD_BY_QUALIFIER: Record<string, GitHubFieldKey> = { label: 'labels', assignee: 'assignee', milestone: 'milestone' }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

// ── compile ────────────────────────────────────────────────────────────────

/** A value as search text: bare when it is one plain word, quoted
 * otherwise. GitHub search has no way to escape a quote inside quotes, so a
 * quote in a value is dropped rather than ending the value early. */
function searchValue(v: string): string {
  const clean = v.replace(/"/g, '')
  return /^[A-Za-z0-9._\-/]+$/.test(clean) ? clean : `"${clean}"`
}

function compileCondition(c: Condition): string | null {
  const cfg = FIELD_CONFIGS[c.field as GitHubFieldKey]
  if (!cfg) return null
  const q = cfg.qualifier
  const vals = c.values.map((v) => v.trim()).filter((v) => v.replace(/"/g, '').length > 0)
  switch (c.op) {
    case 'isEmpty':
      return `no:${q}`
    case 'isAnyOf':
    case 'isNotAnyOf': {
      if (vals.length === 0) return null
      const neg = c.op === 'isNotAnyOf'
      const terms = vals.map((v) => `${neg ? '-' : ''}${q}:${searchValue(v)}`)
      return terms.length === 1 ? terms[0]! : `(${terms.join(neg ? ' AND ' : ' OR ')})`
    }
    case 'is': {
      const written = (cfg.key === 'state' ? STATE_VALUES : REASON_VALUES)[vals[0] ?? '']
      return written ? `${q}:${written}` : null
    }
    case 'before':
    case 'after': {
      const date = vals[0] ?? ''
      return ISO_DATE.test(date) ? `${q}:${c.op === 'before' ? '<' : '>'}${date}` : null
    }
    default:
      // `inLast` and other operators GitHub search can't express.
      return null
  }
}

/** The filter fragment for a model. Advanced text is returned as typed;
 * incomplete conditions are skipped so the live count still works. */
export function compile(model: ScopeFilterModel): string {
  if (model.mode === 'advanced') return model.text.trim()
  const clauses = model.conditions.map(compileCondition).filter((c): c is string => c !== null)
  return clauses.join(model.match === 'any' ? ' OR ' : ' AND ')
}

// ── parse ──────────────────────────────────────────────────────────────────

/**
 * Split on top-level ` AND ` / ` OR `, outside quotes and parentheses.
 * GitHub's operators are upper case only; a lower-case "and" is search
 * text. Not shared with the JQL splitter on purpose: JQL escapes quotes with
 * a backslash and GitHub search has no escape, so one splitter would misread
 * one of the two.
 */
function splitTopLevel(s: string): { joiner: 'AND' | 'OR' | null; parts: string[] } | 'mixed' {
  const parts: string[] = []
  let depth = 0
  let inQuote = false
  let cur = ''
  let joiner: 'AND' | 'OR' | null = null
  for (let i = 0; i < s.length; i++) {
    const ch = s[i]!
    if (ch === '"') inQuote = !inQuote
    else if (!inQuote && ch === '(') depth++
    else if (!inQuote && ch === ')') depth--
    if (!inQuote && depth === 0) {
      const m = /^ (AND|OR) /.exec(s.slice(i))
      if (m) {
        const j = m[1] as 'AND' | 'OR'
        if (joiner && joiner !== j) return 'mixed'
        joiner = j
        parts.push(cur)
        cur = ''
        i += m[0].length - 1
        continue
      }
    }
    if (depth < 0) return 'mixed'
    cur += ch
  }
  if (inQuote || depth !== 0) return 'mixed'
  parts.push(cur)
  return { joiner, parts }
}

/** One `qualifier:value` term, or null. */
function parseTerm(t: string): { neg: boolean; qualifier: string; value: string } | null {
  const m = /^(-?)([a-z]+):(?:"([^"]*)"|([^\s"()]+))$/.exec(t)
  if (!m) return null
  const value = m[3] ?? m[4] ?? ''
  return value ? { neg: m[1] === '-', qualifier: m[2]!, value } : null
}

/** Read one condition back, in exactly the shapes `compileCondition` writes. */
function parseClause(clause: string): Condition | null {
  const text = clause.trim()
  // A grouped list: (label:a OR label:b) or (-label:a AND -label:b).
  const group = /^\((.*)\)$/.exec(text)
  if (group) {
    const inner = splitTopLevel(group[1]!)
    if (inner === 'mixed' || inner.parts.length < 2) return null
    const terms = inner.parts.map((p) => parseTerm(p.trim()))
    if (terms.some((x) => !x)) return null
    const first = terms[0]!
    const field = SET_FIELD_BY_QUALIFIER[first.qualifier]
    if (!field) return null
    if (!terms.every((x) => x!.qualifier === first.qualifier && x!.neg === first.neg)) return null
    // Negations join with AND, a list of alternatives with OR.
    if (inner.joiner !== (first.neg ? 'AND' : 'OR')) return null
    return { field, op: first.neg ? 'isNotAnyOf' : 'isAnyOf', values: terms.map((x) => x!.value) }
  }
  const term = parseTerm(text)
  if (!term) return null
  if (term.qualifier === 'no' && !term.neg) {
    const field = SET_FIELD_BY_QUALIFIER[term.value]
    return field ? { field, op: 'isEmpty', values: [] } : null
  }
  const setField = SET_FIELD_BY_QUALIFIER[term.qualifier]
  if (setField) return { field: setField, op: term.neg ? 'isNotAnyOf' : 'isAnyOf', values: [term.value] }
  if (term.neg) return null
  if (term.qualifier === 'is' || term.qualifier === 'reason') {
    const table = term.qualifier === 'is' ? STATE_VALUES : REASON_VALUES
    const shown = Object.keys(table).find((k) => table[k] === term.value)
    return shown ? { field: term.qualifier === 'is' ? 'state' : 'reason', op: 'is', values: [shown] } : null
  }
  if (term.qualifier === 'created' || term.qualifier === 'updated') {
    const d = /^([<>])(\d{4}-\d{2}-\d{2})$/.exec(term.value)
    return d ? { field: term.qualifier, op: d[1] === '<' ? 'before' : 'after', values: [d[2]!] } : null
  }
  return null
}

/** A saved GitHub filter as builder conditions, or the Advanced view with
 * the text exactly as saved when the builder can't show it. */
export function parse(scopeFilter: string | undefined | null): ScopeFilterModel {
  const text = (scopeFilter ?? '').trim()
  if (text === '') return { mode: 'builder', match: 'all', conditions: [] }
  const split = splitTopLevel(text)
  if (split === 'mixed') return { mode: 'advanced', text }
  const conditions: Condition[] = []
  for (const part of split.parts) {
    const c = parseClause(part)
    if (!c) return { mode: 'advanced', text }
    conditions.push(c)
  }
  const model: BuilderModel = { mode: 'builder', match: split.joiner === 'OR' ? 'any' : 'all', conditions }
  // Only text the builder would write the same way opens as conditions, so
  // reopening can never change a saved filter by a single character.
  return compile(model) === text ? model : { mode: 'advanced', text }
}

/**
 * The label and milestone names written in GitHub search text, so a filter
 * shown in the Advanced view can be checked like one shown as conditions.
 * Finds `label:` and `milestone:` terms, negated or not, outside quotes; a
 * value is one quoted string (`label:"area: auth"`) or a bare word, and a
 * bare word may list names with commas (`label:a,b`). Reads only: the text
 * is never changed.
 */
export function savedValueNames(text: string): { labels: string[]; versions: string[] } {
  const labels = new Set<string>()
  const versions = new Set<string>()
  let inQuote = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!
    if (ch === '"') { inQuote = !inQuote; continue }
    if (inQuote) continue
    // A term starts the text, or follows a space or an opening parenthesis.
    if (i > 0 && !/[\s(]/.test(text[i - 1]!)) continue
    const m = /^-?(label|milestone):/.exec(text.slice(i))
    if (!m) continue
    let j = i + m[0].length
    const names: string[] = []
    if (text[j] === '"') {
      const end = text.indexOf('"', j + 1)
      if (end < 0) break // an unclosed quote: nothing after it is a term
      names.push(text.slice(j + 1, end))
      j = end + 1
    } else {
      const bare = /^[^\s"()]+/.exec(text.slice(j))?.[0] ?? ''
      names.push(...bare.split(','))
      j += bare.length
    }
    for (const n of names.map((x) => x.trim()).filter(Boolean)) (m[1] === 'label' ? labels : versions).add(n)
    i = j - 1
  }
  return { labels: [...labels], versions: [...versions] }
}

export const githubSearchTranslator: ScopeFilterTranslator = {
  languageName: 'GitHub search',
  fields: GITHUB_FIELDS,
  compile,
  parse,
  checksSavedValues: true,
  savedValueNames,
  peopleValuesReadable: true,
}
